const transactionMethods = {
  card: 'Credit Card',
  pay_pal: 'PayPal',
};

export default (store) => {
  store.registerModule('dataLayer', {
    namespaced: true,

    state: {
      registered: [],
    },

    getters: {
      checkoutStep: () => (step) => ({
        prefix: 'eec.',
        ecommerce: {
          checkout: {
            actionField: { step },
          },
        },
      }),

      addToCart: () => (value) => ({
        prefix: 'eec.',
        ecommerce: {
          currencyCode: value.currency || 'USD',
          add: {
            products: [{
              name: value.planTitle || value.title,
              id: value.extId,
              price: value.discountPrice || value.price,
              quantity: 1,
            }],
          },
        },
      }),

      transaction: (state, getters, rootState) => (value) => {
        const { checkout: { order, paymentInfo } } = rootState;

        const ecommerce = {
          currencyCode: order.currency,
          purchase: {
            actionField: {
              id: value, // transaction ID
              revenue: order.total - order.tax, // total sum paid in checkout
              tax: order.tax,
              option: transactionMethods[paymentInfo.method] || 'Credit Card', // payment method chosen (PayPal, Credit Card, Apple Pay etc.)
              coupon: order.coupon.value || '',
            },
            products: [{
              name: order.plan.name, // Product name in Chargify
              id: order.plan.extId, // Product ID in Chargify
              price: order.plan.price,
              quantity: order.quantity || 1, // 2+ in B2B checkouts, should reflect number of subscriptions
              coupon: order.coupon.value || '',
              dimension9: order.b2b ? 'B2B' : 'B2C', // variants: B2C, B2B
            }],
          },
        };

        return { prefix: 'eec.', ecommerce };
      },

      promoImpression: () => (value) => {
        let position = 'Top Banner';
        const namedRoutes = {
          'qa-hub': 'Q&A Hub Sidebar',
          index: 'User Dashboard',
        };

        if (['qa-hub', 'index'].includes(value.route) && value.type !== 2) {
          position = namedRoutes[value.route];
        }

        const ecommerce = {
          promoView: {
            promotions: [
              {
                name: value.typeName, // Always, Promo, Trigger
                creative: value.planTitle, // the plan that is discounted with the current banner
                position, // variants: Top Banner, User Dashboard, Q&A Hub Sidebar; check note about positions above
              },
            ],
          },
        };

        return { prefix: 'eec.', ecommerce };
      },

      promotionClick: () => (value) => {
        let position = 'Top Banner';
        const namedRoutes = {
          'qa-hub': 'Q&A Hub Sidebar',
          index: 'User Dashboard',
        };

        if (['qa-hub', 'index'].includes(value.route) && value.type !== 2) {
          position = namedRoutes[value.route];
        }

        return {
          prefix: 'eec.',
          ecommerce: {
            promoClick: {
              promotions: [
                {
                  name: value.typeName,
                  creative: value.planTitle,
                  position,
                }],
            },
          },
        };
      },

      lp_form_submissions: () => (value) => ({
        action: value,
      }),

      checkout: () => (value) => ({
        prefix: 'eec.',
        ecommerce: {
          checkout: {
            actionField: { step: value.step },
            products: [{
              name: value.planTitle || value.title,
              id: value.extId,
              price: value.discountPrice || value.price,
              quantity: 1,
            }],
          },
        },
      }),
    },

    mutations: {
      setRegistered(state, payload) {
        state.registered.push(payload);
      },

      resetRegistered(state) {
        state.registered = [];
      },

      removeRegistered(state, payload) {
        state.registered = state.registered.filter((v) => v.event !== payload);
      },
    },

    actions: {
      addEvent({
        state, rootState, commit, getters,
      }, payload) {
        const eventID = payload.eventID || `${rootState?.tracking?.sessionId || ''}${Date.now()}`;

        let { event } = payload;
        let obj = { event, eventID };

        if (typeof getters[payload.event] !== 'undefined') {
          event = getters[payload.event](payload.value);
          const { prefix } = event;

          delete event.prefix;

          obj = {
            event: `${prefix || ''}${payload.event}`,
            ...event,
            eventID,
          };
        } else if (payload.data) {
          obj = { ...obj, ...payload.data };
        }

        if (state.registered.find((v) => v.event === payload.event && v.value === payload.value)) {
          return;
        }

        commit('setRegistered', payload);

        // Optional - page title and referrer
        if (process.client) {
          // Get meta title
          const metaTitleElement = document.querySelector('title');
          // const projectReferrer = this.$tracking.getProjectReferrerCookie(payload.referrerFromThisProject);
          const referrer = rootState.referrer || document.referrer;

          let pageTitle = document.title;
          if (metaTitleElement) {
            pageTitle = metaTitleElement.innerText;
          }

          if (pageTitle) {
            obj.pageTitle = pageTitle;
          }

          // document.referrer is needed here to avoid sending wrong referrer when user refreshes the page
          // and referrer cookie presented
          if (!obj.pageReferrer && referrer) {
            obj.pageReferrer = referrer;

          // Catch default right referrer when user comes from different website
          } else if (!obj.pageReferrer && payload.initialPage && document.referrer) {
            obj.pageReferrer = document.referrer;
          }
        }

        if (payload.event === 'Pageview') {
          obj.pagePath = window.location.pathname + window.location.search;
        }

        if (payload.event === 'checkout') {
          dataLayer.push({ ecommerce: null });
        }

        if (payload.event === 'transaction' && payload.additionalParams) {
          obj.user_data = payload.additionalParams;
        }

        const userId = rootState?.auth?.user?.id;
        if (userId) {
          obj['User-ID'] = `user${userId}`;
        }

        dataLayer.push(obj);

        if (obj.event === 'eec.addToCart' || obj.event === 'eec.transaction' || obj.event === 'BlogSubmission') {
          const names = { 'eec.addToCart': 'Initiate checkout', 'eec.transaction': 'Purchase', BlogSubmission: 'EmailLead' };

          this.$tracking.sendEvent({
            event: obj.event,
            dataLayerEvent: names[obj.event],
            destinationUrl: window.location.href,
            eventID,
            capi: true,
            ...(obj.event === 'eec.transaction' && { currency: rootState?.checkout?.order.currency }),
            ...(obj.event === 'eec.transaction' && { value: rootState?.checkout?.order.total - rootState?.checkout?.order.tax }),
            userAgent: navigator.userAgent,
          });
        }
      },
    },
  });
};
